/**
 * @fileoverview Top-level minimization driver.
 *
 * Orchestrates the combined evaluator (`forces/evaluator.js`), the L-BFGS
 * direction generator (`minimizer/lbfgs.js`), and the backtracking Armijo line
 * search (`minimizer/lineSearch.js`) into a single `minimize()` call that
 * returns a `MinimizationResult`.
 *
 * Algorithm:
 *   1. Evaluate U and grad at the current point.
 *   2. Zero the gradient on frozen atoms (so L-BFGS treats them as inert and
 *      the line search never moves them).
 *   3. Convergence test (OpenMM LocalEnergyMinimizer-compatible): the RMS force
 *      norm `||grad||_2 / sqrt(nAtoms) <= forceTolerance` -> done. This is the
 *      L2/RMS criterion OpenMM uses (`||g||_2 <= tolerance*sqrt(numParticles)`),
 *      not a max-component (inf-norm) test: the inf-norm keys on the single
 *      worst component and is hypersensitive to single-precision (f32 WASM)
 *      gradient noise, whereas the RMS averages over all 3N components and
 *      matches the reference engine.
 *   4. L-BFGS direction d = -H.g (steepest descent on empty/just-reset history).
 *   5. Backtracking line search along d.
 *      - On success: accept the new point, push the (s, y) curvature pair.
 *      - On failure: reset L-BFGS, take one steepest-descent step, then retry.
 *        If steepest descent also fails, the energy is flat to the force
 *        precision -- the structure is at the resolvable minimum. Stop and
 *        report success (terminationReason 'stalled'), matching OpenMM's
 *        LocalEnergyMinimizer (which returns a line-search stall as success).
 *        The only genuine non-success finite outcome is exhausting maxIterations.
 *
 * Frozen atoms: their gradient components are zeroed *before* L-BFGS sees the
 * gradient, on every evaluation. Since d is built from a gradient that is zero
 * on those components (and the steepest-descent fallback divides by the same
 * gradient), the step direction is also zero there -- frozen coordinates never
 * move. The (s, y) pairs likewise have zero entries on frozen atoms.
 *
 * Units: nm, kJ/mol, e, rad. Convergence tolerance (forceTolerance) is the RMS
 * force in kJ/mol/nm (OpenMM-style; see the convergence test above).
 *
 * Hot-path discipline: all working vectors are preallocated once before the
 * iteration loop. The only per-call allocations are the result buffers and the
 * L-BFGS/evaluator instances built at entry.
 *
 * @see proto/src/chemistry/LocalMinimizer/types.js (MinimizationOptions / MinimizationResult).
 */

import { LBFGS } from './lbfgs.js';
import { backtrackingLineSearch, strongWolfeLineSearch } from './lineSearch.js';
import { Evaluator } from '../forces/evaluator.js';

/**
 * @typedef {import('../types.js').LigandInput} LigandInput
 * @typedef {import('../types.js').MinimizationOptions} MinimizationOptions
 * @typedef {import('../types.js').MinimizationResult} MinimizationResult
 * @typedef {import('../forces/receptor.js').ReceptorContext} ReceptorContext
 */

/** Default L-BFGS iteration cap (types.js MinimizationOptions.maxIterations). */
const DEFAULT_MAX_ITERATIONS = 500;
/** Default convergence tolerance, kJ/mol/nm (types.js forceTolerance). This is
 *  the RMS force norm threshold (OpenMM LocalEnergyMinimizer default is also
 *  10 kJ/mol/nm RMS), not a max-component threshold. See gradRms() / the
 *  convergence test in the file header. */
const DEFAULT_FORCE_TOLERANCE = 10;
/** Default L-BFGS history size m (types.js historySize).
 *  Set to 50 rather than the canonical Liu-Nocedal 5: real-ligand internal-
 *  energy surfaces (dozens of atoms, ~100+ torsions) need much more curvature
 *  memory to drive the gradient down before the line search stalls. At small
 *  history the gradient plateaus high; m=50 reaches a far deeper minimum in a
 *  bounded budget. */
const DEFAULT_HISTORY_SIZE = 50;

/**
 * Largest absolute component of a vector (the inf-norm). Retained as a
 * diagnostic only (`finalGradMaxAbs`); convergence is the RMS force norm
 * `gradRms`, not this max-component test.
 *
 * @param {Float64Array} v
 * @returns {number}
 */
function maxAbs(v) {
    let m = 0;
    for (let i = 0; i < v.length; i++) {
        const a = v[i] < 0 ? -v[i] : v[i];
        if (a > m) m = a;
    }
    return m;
}

/**
 * RMS force norm used for convergence, matching OpenMM's LocalEnergyMinimizer:
 * OpenMM stops when `||g||_2 <= tolerance * sqrt(numParticles)`, i.e. when
 * `||g||_2 / sqrt(nAtoms) <= tolerance`. This returns that left-hand side,
 * `sqrt( sum_i g_i^2 / nAtoms )` over all 3*nAtoms components (frozen atoms
 * contribute 0 to the sum and still count in nAtoms, as OpenMM counts all
 * particles). Comparable directly against forceTolerance (kJ/mol/nm).
 *
 * @param {Float64Array} v       gradient, length 3*nAtoms
 * @param {number} nAtoms
 * @returns {number}
 */
function gradRms(v, nAtoms) {
    let s = 0;
    for (let i = 0; i < v.length; i++) s += v[i] * v[i];
    return Math.sqrt(s / nAtoms);
}

/**
 * Zero the gradient components belonging to frozen atoms, in place.
 *
 * @param {Float64Array} grad        Flat length 3*N gradient.
 * @param {Int32Array}   frozenMask  Length N; 1 if atom is frozen, else 0.
 * @returns {void}
 */
function applyFrozenMask(grad, frozenMask) {
    for (let a = 0; a < frozenMask.length; a++) {
        if (frozenMask[a]) {
            const a3 = 3 * a;
            grad[a3] = 0;
            grad[a3 + 1] = 0;
            grad[a3 + 2] = 0;
        }
    }
}

/**
 * Minimize a ligand's potential energy with L-BFGS + Armijo line search.
 *
 * The receptor (if any) is supplied via a prepared {@link ReceptorContext};
 * it is held fixed (only the ligand-side gradient is formed). Receptor-free
 * (vacuum) minimization is supported by passing `null`.
 *
 * @param {LigandInput} ligand                 Topology + parameters + starting coords.
 * @param {ReceptorContext|null} receptorContext  Prepared receptor or null.
 * @param {MinimizationOptions} [options]
 * @returns {MinimizationResult}
 */
export function minimize(ligand, receptorContext = null, options = {}) {
    const t0 = (typeof performance !== 'undefined' && performance.now)
        ? performance.now() : Date.now();

    const nAtoms = ligand.topology.nAtoms;
    const n = 3 * nAtoms;

    const maxIterations = options.maxIterations ?? DEFAULT_MAX_ITERATIONS;
    const forceTolerance = options.forceTolerance ?? DEFAULT_FORCE_TOLERANCE;
    const historySize = options.historySize ?? DEFAULT_HISTORY_SIZE;
    const reportInterval = options.reportInterval;
    const onIteration = options.onIteration;
    // Pick the line-search kernel. Default 'armijo' preserves the engine's
    // historical convergence behavior; 'wolfe' enforces the strong Wolfe
    // curvature condition (positive s.y for the L-BFGS update -- typically
    // converges in ~half the iters with much smaller historySize).
    const lineSearchMode = options.lineSearch ?? 'armijo';
    const lineSearchFn = lineSearchMode === 'wolfe'
        ? strongWolfeLineSearch
        : backtrackingLineSearch;
    // Optional override of the line search's internal step budget (Wolfe's
    // bracket+zoom cap / Armijo's backtrack cap; OpenMM uses 40). When unset the
    // line search keeps its own default (Wolfe 25 / Armijo 20). `lsOpts` is built
    // below, once the scratch buffer it references exists.
    const lineSearchMaxIter = options.lineSearchMaxIter;

    // Frozen-atom mask (per-atom, length nAtoms). 1 = frozen.
    const frozenMask = new Int32Array(nAtoms);
    if (Array.isArray(options.frozenLigandAtoms)) {
        for (const a of options.frozenLigandAtoms) {
            if (a >= 0 && a < nAtoms) frozenMask[a] = 1;
        }
    }

    // Combined evaluator (owns its reused gradient buffer). The evalFn contract
    // the line search expects: coords -> { energy, gradient }.
    //
    // `options.evaluator` lets a caller inject an alternative backend (the WASM
    // evaluator) that exposes the same evaluate(coords) -> { energy, gradient }
    // surface. When absent (the default, and the only path the JS callers take),
    // the JS Evaluator is built, leaving the reference path unchanged. The
    // injected evaluator must already have been constructed with the same
    // ligand / receptorContext / restraint options.
    const evaluator = options.evaluator || new Evaluator(ligand, receptorContext, options);
    let evalCount = 0;
    const evalFn = (coords) => {
        evalCount++;
        return evaluator.evaluate(coords);
    };

    const lbfgs = new LBFGS(n, historySize);

    // Preallocated working vectors (no per-iteration allocation).
    const x = Float64Array.from(ligand.coords); // current point (owned, mutated)
    const g = new Float64Array(n); // current gradient (frozen-masked)
    const dir = new Float64Array(n); // search direction
    const sBuf = new Float64Array(n); // x_{k+1} - x_k
    const yBuf = new Float64Array(n); // g_{k+1} - g_k
    const prevX = new Float64Array(n);
    const prevG = new Float64Array(n);
    const lsScratch = new Float64Array(n); // line-search trial point

    // Line-search options (scratch + optional step-budget override). Reused
    // across both line-search calls per iteration (they run sequentially).
    const lsOpts = { scratch: lsScratch };
    if (lineSearchMaxIter != null) {
        lsOpts.maxIterations = lineSearchMaxIter; // strong Wolfe bracket+zoom cap
        lsOpts.maxBacktracks = lineSearchMaxIter; // Armijo backtrack cap
    }

    /**
     * Evaluate at `coords`, copy the frozen-masked gradient into `gOut`, and
     * return the energy. The evaluator's gradient buffer is transient (reused),
     * so we copy out what we want to retain.
     *
     * @param {Float64Array} coords
     * @param {Float64Array} gOut
     * @returns {number} energy
     */
    function evalInto(coords, gOut) {
        const { energy, gradient } = evalFn(coords);
        gOut.set(gradient);
        applyFrozenMask(gOut, frozenMask);
        return energy;
    }

    let f = evalInto(x, g);
    let gInf = maxAbs(g); // inf-norm, retained for diagnostics
    let gRms = gradRms(g, nAtoms); // RMS force norm, the convergence metric

    let iterations = 0;
    let converged = gRms <= forceTolerance;
    // OpenMM-style termination: a line-search stall (no step along the current,
    // possibly noise-limited gradient decreases the energy) means the structure
    // is minimized as far as the force precision allows -- OpenMM's
    // LocalEnergyMinimizer returns that as success, not a failure. We track it
    // separately so it does not get reported as non-convergence (the only
    // genuinely-unsuccessful finite outcomes are exhausting the iteration cap
    // while still making progress, or a non-finite state).
    let stalled = false;
    /** @type {string|undefined} */
    let errorMsg;

    if (onIteration) onIteration(0, f, gInf);

    while (!converged && iterations < maxIterations) {
        // Guard against a non-finite state (e.g. NaN from a singular geometry).
        // Test gRms, not gInf: maxAbs() (which yields gInf) skips a NaN component
        // (`NaN > m` is false) and returns finite, so a NaN gradient would slip past
        // as a false 'stalled' success. gRms sums g_i^2, so NaN propagates and is caught.
        if (!Number.isFinite(f) || !Number.isFinite(gRms)) {
            errorMsg = 'NaN in gradient';
            break;
        }

        lbfgs.computeDirection(g, dir);

        prevX.set(x);
        prevG.set(g);

        let res = lineSearchFn(evalFn, x, f, g, dir, lsOpts);

        if (!res.ok) {
            // Line-search failure: discard the (now-stale) curvature history and
            // take one steepest-descent step before retrying.
            lbfgs.reset();
            lbfgs.computeDirection(g, dir); // empty history -> -g/||g||_inf
            res = lineSearchFn(evalFn, x, f, g, dir, lsOpts);
            if (!res.ok) {
                // Even steepest descent cannot make progress: the energy is flat
                // (to the force precision) along every tried direction, so we are
                // at the minimum the engine can resolve. Stop and report success
                // (OpenMM-style), not non-convergence -- see `stalled` above.
                stalled = true;
                break;
            }
        }

        // Accept the step. The line search returns the gradient at the new
        // point in res.gradNew (aliasing the evaluator buffer) -- copy + mask it.
        x.set(res.xNew);
        f = res.fNew;
        g.set(res.gradNew);
        applyFrozenMask(g, frozenMask);

        // Push the curvature pair (s = dx, y = dg). The curvature guard inside
        // LBFGS.update silently skips a pair that would break positive
        // definiteness -- no special handling needed here.
        for (let i = 0; i < n; i++) {
            sBuf[i] = x[i] - prevX[i];
            yBuf[i] = g[i] - prevG[i];
        }
        lbfgs.update(sBuf, yBuf);

        iterations++;
        gInf = maxAbs(g);
        gRms = gradRms(g, nAtoms);
        converged = gRms <= forceTolerance;

        if (onIteration && reportInterval && iterations % reportInterval === 0) {
            onIteration(iterations, f, gInf);
        }
    }

    // Final callback so the caller always sees the terminal state, unless the
    // last in-loop report already fired at exactly this iteration count. With no
    // reportInterval there were no in-loop reports, so it always fires.
    const alreadyReportedFinal = reportInterval != null
        && iterations % reportInterval === 0;
    if (onIteration && !alreadyReportedFinal) {
        onIteration(iterations, f, gInf);
    }

    const t1 = (typeof performance !== 'undefined' && performance.now)
        ? performance.now() : Date.now();

    // Classify the terminal state (OpenMM-style). A non-finite state is the only
    // hard error; a stall or gradient convergence is a success; exhausting the
    // iteration cap while still progressing is the one genuine non-convergence.
    let terminationReason;
    if (errorMsg) terminationReason = 'nonFinite';
    else if (converged) terminationReason = 'converged';
    else if (stalled) terminationReason = 'stalled';
    else terminationReason = 'maxIterations';
    const success = terminationReason === 'converged' || terminationReason === 'stalled';

    /** @type {MinimizationResult} */
    const result = {
        coords: x,
        energy: f,
        iterations,
        converged,
        success,
        terminationReason,
        finalGradMaxAbs: gInf,
        finalGradRms: gRms,
        evalCount,
        wallTimeMs: t1 - t0,
        activeBackend: options.activeBackend || 'cpu',
    };
    if (!success) {
        result.error = errorMsg
            || 'minimization did not converge within the iteration limit';
    }
    return result;
}
